import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {useMediaQuery} from 'src/hooks/useMediaQuery';
import {Draggable} from '@hello-pangea/dnd';
import Button from 'src/components/button';
import NewTabTooltip from 'shared/components/new-tab-tooltip';
import CollegeApplicationStatusMenu from 'shared/college-application-status/components/college-application-status-menu';
import Icon from 'shared/components/icon';
import {EARNINGS_PROP_TYPES} from 'shared/college-earnings/utils';
import {ADMISSION_LIKELIHOOD_CATEGORY_PROP_TYPE} from '../prop-types';
import LikelihoodAndEarningsLabels from './likelihood-and-earnings-labels';
import CollegeFacts from './college-facts';
import styles from './college-row.module.scss';
import {Link} from 'react-router-dom';

const propTypes = {
  name: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  bannerImage: PropTypes.string,
  logoImage: PropTypes.string,
  index: PropTypes.number,
  isLast: PropTypes.bool,
  collegeCategory: PropTypes.oneOf(['Public', 'Private']),
  collegeFacts: PropTypes.shape(CollegeFacts.propTypes),
  earnings: PropTypes.shape(EARNINGS_PROP_TYPES).isRequired,
  admissionLikelihoodCategory: ADMISSION_LIKELIHOOD_CATEGORY_PROP_TYPE,
  applicationWebsite: PropTypes.string,
  applicationStatus: PropTypes.shape({
    status: PropTypes.string.isRequired,
  }),
  setCollegeAppStatus: PropTypes.func,
  isApplyButtonShown: PropTypes.bool,
  isApplicationStatusMenuShown: PropTypes.bool,
  draggable: PropTypes.bool,
  isBalancedListInfoAvailable: PropTypes.bool,
};

const CollegeRow = ({
  admissionLikelihoodCategory,
  applicationStatus,
  applicationWebsite,
  bannerImage,
  collegeFacts,
  draggable,
  earnings,
  isApplicationStatusMenuShown,
  isApplyButtonShown,
  index,
  isLast,
  location,
  logoImage,
  name,
  setCollegeAppStatus,
  slug,
  isBalancedListInfoAvailable,
}) => {
  const mq = useMediaQuery();

  const inlineStatusMenu = isApplicationStatusMenuShown && !isApplyButtonShown && mq.XS;
  const footerApplicationStatusMenu = isApplicationStatusMenuShown && !inlineStatusMenu;

  const showActions = isApplyButtonShown || footerApplicationStatusMenu;
  const showFooter = collegeFacts || showActions;
  const footerClass = showActions ? styles.footer : styles.footerNoActions;

  const display = (
    <>
      <div className={styles.content}>
        <Link to={`/edu/${slug}`} className={styles.body}>
          <div className={styles.imageContainer}>
            <img className={styles.bannerImage} src={bannerImage} alt={`${name} banner`} />
            <img className={styles.logoImage} src={logoImage} alt={`${name} logo`} />
          </div>
          <div className={styles.titleAndPillsContainer}>
            <div className={styles.name} data-testid="college-name">
              {name}
            </div>
            <div>{location}</div>
            <LikelihoodAndEarningsLabels
              isBalancedListInfoAvailable={isBalancedListInfoAvailable}
              admissionLikelihoodCategory={admissionLikelihoodCategory}
              earnings={earnings}
              isEarningStatusShown
              isApplicationStatusMenuShown={inlineStatusMenu}
            />
          </div>
        </Link>
        {showFooter && (
          <div
            className={footerClass}
            data-testid={
              showActions ? 'college-row-footer-actions' : 'college-row-footer-no-actions'
            }
          >
            {collegeFacts && (
              <CollegeFacts
                collegeCategory={collegeFacts.collegeCategory}
                distanceMiles={collegeFacts.distanceMiles}
                yearlyNetPriceDollars={collegeFacts.yearlyNetPriceDollars}
              />
            )}
            {showActions && (
              <div className={styles.footerActions}>
                {isApplyButtonShown && (
                  <NewTabTooltip>
                    <a
                      className={styles.applyButtonLink}
                      href={applicationWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        className={styles.applyButton}
                        width={96}
                        margin={false}
                        size="tiny"
                        outline
                        testId="apply-button"
                      >
                        Apply
                      </Button>
                    </a>
                  </NewTabTooltip>
                )}
                {footerApplicationStatusMenu && (
                  <CollegeApplicationStatusMenu
                    college={{_id: earnings._id}}
                    collegeApplicationStatus={applicationStatus}
                    setCollegeAppStatus={setCollegeAppStatus}
                    showStatusContent={false}
                    forceCompactView
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.dragHandle}>
        {draggable && <Icon testid="draggable" iconType="grip-v-r" />}
      </div>
    </>
  );

  return (
    <div data-testid="college-row">
      <Draggable draggableId={earnings._id} index={index} isDragDisabled={!draggable}>
        {(provided, snapshot) => {
          const conditionalClasses = {
            [styles.draggable]: draggable,
            [styles.last]: isLast,
            [styles.dragging]: snapshot.isDragging,
          };
          return (
            <div
              className={classnames(styles.collegeRow, conditionalClasses)}
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              {display}
            </div>
          );
        }}
      </Draggable>
    </div>
  );
};

const Loading = () => (
  <div className={styles.collegeRow}>
    <div className={styles.body}>
      <div className={styles.imageContainer}>
        <div className={classnames(styles.bannerImage, 'shared-shimmer')} />
      </div>
      <div className={styles.titleAndPillsContainer}>
        <div className={classnames(styles.name, 'shared-shimmer-text', 'medium')}></div>
        <div className="shared-shimmer-text x-small"></div>
      </div>
    </div>
  </div>
);

CollegeRow.propTypes = propTypes;
CollegeRow.Loading = Loading;

export default CollegeRow;
